import {DatePicker} from '@ark-ui/react/date-picker';
import {faCalendar} from '@fortawesome/pro-light-svg-icons/faCalendar';
import {faChevronLeft} from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import {faChevronRight} from '@fortawesome/pro-light-svg-icons/faChevronRight';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import type {DateValue} from '@internationalized/date';
import {parseDate} from '@internationalized/date';
import {getDateFormatString} from '@utils';
import {isMatch, parse} from 'date-fns';
import type {ChangeEvent, FocusEventHandler, KeyboardEventHandler, Ref} from 'react';
import {useMemo, useState} from 'react';

import {useTableFiltersStyleConfig} from '../../theme-config';
import {IconButton} from '../IconButton';

import {calendarDateFormatter, formatDateToISO} from './helpers';

import {
  Box,
  Circle,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  forwardRef,
} from '@chakra-ui/react';

type InlineDatePickerProps = {
  date?: string;
  setDate: (value: string) => void;
  onKeyDown: KeyboardEventHandler;
  preventCloseOnSelect?: boolean;
  isOpen?: boolean;
  size?: 'sm' | 'md' | 'lg';
};

const InlineDatePickerNew = forwardRef((props: InlineDatePickerProps, ref: Ref<HTMLInputElement>) => {
  const {date, setDate, onKeyDown, isOpen, preventCloseOnSelect, size = `sm`} = props;

  const todayAsCalendarDate = useMemo(() => {
    const today = new Date();
    return parseDate(today.toISOString().split(`T`)[0]);
  }, []);

  const dateValue = date?.trim() ? [date] : [];
  // TODO should we make these more generic?
  const inputCss = useTableFiltersStyleConfig(`searchInput`);

  const [open, setOpen] = useState(isOpen ?? false);

  const focusHandler: FocusEventHandler<HTMLDivElement> = event => {
    if (event.target.getAttribute(`data-part`) === `input` && open) {
      if (preventCloseOnSelect) {
        return;
      }
      setOpen(false);
    }
  };

  const calendarClickHandler = () => {
    if (preventCloseOnSelect) {
      return;
    }
    setOpen(!open);
  };

  const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const format = getDateFormatString();
    if (isMatch(value, format)) {
      const parsedDate = parse(value, format, new Date());
      const formattedISODate = formatDateToISO(parsedDate);
      setDate(formattedISODate);
    }
  };

  const valueChangeHandler = ({value}: {value: Array<DateValue>}) => {
    const [date] = value[0].toString().split(`T`);
    setDate(date);
  };

  return (
    <DatePicker.Root
      closeOnSelect={false}
      open={open}
      format={calendarDateFormatter}
      value={dateValue}
      onValueChange={valueChangeHandler}
      onFocus={focusHandler}
      onChange={changeHandler}
      onKeyDown={onKeyDown}
    >
      <DatePicker.Control asChild>
        <InputGroup size={size}>
          <DatePicker.Input asChild>
            <Input size={size} sx={inputCss} ref={ref} placeholder={getDateFormatString().toLowerCase()} />
          </DatePicker.Input>

          {!preventCloseOnSelect && (
            <DatePicker.Trigger asChild>
              <InputRightElement>
                <IconButton
                  aria-label={`calendar`}
                  icon={<FontAwesomeIcon icon={faCalendar} />}
                  size={[`sm`, `md`].includes(size) ? `xs` : `sm`}
                  onClick={calendarClickHandler}
                />
              </InputRightElement>
            </DatePicker.Trigger>
          )}
        </InputGroup>
      </DatePicker.Control>
      <DatePicker.View view={`day`}>
        <DatePicker.Context>
          {api =>
            open ? (
              <Box maxWidth={`100%`}>
                <DatePicker.ViewControl asChild>
                  <Flex
                    justifyContent={`space-between`}
                    alignItems={`center`}
                    minHeight={`64px`}
                    borderBottomStyle={`solid`}
                    borderBottomWidth={`1px`}
                    borderBottomColor={`gray.200`}
                  >
                    <Box
                      sx={{
                        fontSize: `xl`,
                        fontWeight: `semibold`,
                        display: `flex`,
                        alignItems: `center`,
                        cursor: `default`,
                      }}
                    >
                      <DatePicker.RangeText />
                    </Box>
                    <Flex>
                      <Arrow direction={`left`} label={`Previous month`} />
                      <Arrow direction={`right`} label={`Next month`} />
                    </Flex>
                  </Flex>
                </DatePicker.ViewControl>

                <DatePicker.Table asChild>
                  <Table sx={{position: `relative`}}>
                    <DatePicker.TableHead asChild>
                      <Thead>
                        <DatePicker.TableRow asChild>
                          <Tr>
                            {api.weekDays.map((weekDay, id) => (
                              <DatePicker.TableHeader key={id} asChild>
                                <Th sx={{textAlign: `center`, border: 0, fontWeight: `normal`, color: `td`}}>
                                  {weekDay.short.substring(0, 2)}
                                </Th>
                              </DatePicker.TableHeader>
                            ))}
                          </Tr>
                        </DatePicker.TableRow>
                      </Thead>
                    </DatePicker.TableHead>

                    <DatePicker.TableBody asChild>
                      <Tbody>
                        {api.weeks.map((week, id) => (
                          <DatePicker.TableRow key={id} asChild>
                            <Tr>
                              {week.map((day, id) => {
                                const disabled =
                                  api.visibleRange.start.compare(day) > 0 || api.visibleRange.end.compare(day) < 0;
                                const current = api.value?.[0] ? api.value[0].compare(day) === 0 : false;
                                const isToday = day.compare(todayAsCalendarDate) === 0;
                                return (
                                  <DatePicker.TableCell key={id} value={day} asChild>
                                    <Td sx={{textAlign: `center`, border: 0}}>
                                      <DatePicker.TableCellTrigger asChild>
                                        <Circle
                                          sx={{
                                            position: `relative`,
                                            cursor: disabled ? `default` : `pointer`,
                                            backgroundColor: current ? `blue-spark.600` : undefined,
                                            color: disabled ? `blackAlpha.400` : current ? `white` : undefined,
                                            fontSize: `sm`,
                                            height: `1.5rem`,
                                            width: `1.5rem`,
                                            textOverflow: `visible`,
                                            whiteSpace: `nowrap`,
                                            letterSpacing: `-1px`,
                                            _hover:
                                              current || disabled
                                                ? {}
                                                : {
                                                    backgroundColor: `blue-spark.600`,
                                                    color: `white`,
                                                  },
                                          }}
                                        >
                                          <Text>
                                            {day.day}
                                            {isToday && (
                                              <Circle
                                                size={`4px`}
                                                sx={{
                                                  position: `absolute`,
                                                  bottom: `1px`,
                                                  left: `50%`,
                                                  transform: `translateX(-50%)`,
                                                  backgroundColor: current ? `white` : `blue-spark.600`,
                                                }}
                                              />
                                            )}
                                          </Text>
                                        </Circle>
                                      </DatePicker.TableCellTrigger>
                                    </Td>
                                  </DatePicker.TableCell>
                                );
                              })}
                            </Tr>
                          </DatePicker.TableRow>
                        ))}
                      </Tbody>
                    </DatePicker.TableBody>
                  </Table>
                </DatePicker.Table>
              </Box>
            ) : (
              <></>
            )
          }
        </DatePicker.Context>
      </DatePicker.View>
    </DatePicker.Root>
  );
});

export default InlineDatePickerNew;

type ArrowProps = {
  direction: 'left' | 'right';
  label: string;
};
const Arrow = ({direction, label}: ArrowProps) => {
  const icon = direction === `left` ? faChevronLeft : faChevronRight;
  const button = useMemo(
    () => (
      <IconButton
        aria-label={label}
        variant={`unstyled`}
        icon={<FontAwesomeIcon icon={icon} />}
        sx={{
          margin: `2`,
          height: `auto`,
          minHeight: `20px`,
          width: `auto`,
          aspectRatio: `1/1`,
          minWidth: `20px`,
          fontSize: `sm`,
          _hover: {
            backgroundColor: `blackAlpha.100`,
          },
        }}
      />
    ),
    [icon, label],
  );

  return direction === `left` ? (
    <DatePicker.PrevTrigger asChild>{button}</DatePicker.PrevTrigger>
  ) : (
    <DatePicker.NextTrigger asChild>{button}</DatePicker.NextTrigger>
  );
};
